import { apiClient } from './axios';

import type {
  DealerKitRegistration,
  CreateDealerKitRegistrationDto,
  UpdateDealerKitRegistrationDto,
  PaginatedDealerKitWebhookLogs,
  PaginatedDealerKitContacts,
} from '@repo/shared-types';

export const dealerKitApi = {
  getRegistrations: async () => {
    const response = await apiClient.get<DealerKitRegistration[]>('/crm-integration/registrations');
    return response.data;
  },

  createRegistration: async (data: CreateDealerKitRegistrationDto) => {
    const response = await apiClient.post<DealerKitRegistration>(
      '/crm-integration/registrations',
      data,
    );
    return response.data;
  },

  updateRegistration: async (id: string, data: UpdateDealerKitRegistrationDto) => {
    const response = await apiClient.patch<DealerKitRegistration>(
      `/crm-integration/registrations/${id}`,
      data,
    );
    return response.data;
  },

  deleteRegistration: async (id: string) => {
    await apiClient.delete(`/crm-integration/registrations/${id}`);
  },

  getWebhookLogs: async (registrationId: string, page = 1, limit = 20) => {
    const response = await apiClient.get<PaginatedDealerKitWebhookLogs>(
      `/crm-integration/registrations/${registrationId}/logs`,
      { params: { page, limit } },
    );
    return response.data;
  },

  getContacts: async (page = 1, limit = 20, search?: string) => {
    const response = await apiClient.get<PaginatedDealerKitContacts>('/crm-integration/contacts', {
      params: { page, limit, search },
    });
    return response.data;
  },

  deleteContact: async (id: string) => {
    await apiClient.delete(`/crm-integration/contacts/${id}`);
  },

  syncStock: async () => {
    const response = await apiClient.post<DealerKitStockSyncResult>('/crm-integration/stock/sync');
    return response.data;
  },

  getStockSyncState: async () => {
    const response = await apiClient.get<DealerKitStockSyncState | null>(
      '/crm-integration/stock/sync-state',
    );
    return response.data;
  },

  getStockTokens: async () => {
    const response = await apiClient.get<StockApiToken[]>('/crm-integration/stock/tokens');
    return response.data;
  },

  createStockToken: async (name: string) => {
    const response = await apiClient.post<{ token: string; id: string }>(
      '/crm-integration/stock/tokens',
      { name },
    );
    return response.data;
  },

  revokeStockToken: async (id: string) => {
    await apiClient.delete(`/crm-integration/stock/tokens/${id}`);
  },

  searchStock: async (params: {
    query?: string;
    filters?: Record<string, unknown>;
    sort?: string;
    page?: number;
    limit?: number;
    /** 'dealerkit' and/or connector ids; omitted = DealerKit only. */
    sources?: string[];
    postcode?: string;
    radius_miles?: number;
  }) => {
    const response = await apiClient.post<StockSearchResult>(
      '/crm-integration/stock/search/test',
      params,
    );
    return response.data;
  },

  backfillSearchText: async () => {
    const response = await apiClient.post<{ updated: number }>(
      '/crm-integration/stock/search/backfill',
    );
    return response.data;
  },

  getStockListings: async (page = 1, limit = 20, search?: string, source?: string) => {
    const response = await apiClient.get<PaginatedDealerKitStockListings>(
      '/crm-integration/stock/listings',
      { params: { page, limit, search, source } },
    );
    return response.data;
  },

  getStockListing: async (id: string) => {
    const response = await apiClient.get<StockListingDetail>(
      `/crm-integration/stock/listings/${id}`,
    );
    return response.data;
  },

  uploadStockCsv: async (rows: CsvStockRow[]) => {
    const response = await apiClient.post<{ imported: number; errors: string[] }>(
      '/crm-integration/stock/upload-csv',
      { rows },
    );
    return response.data;
  },

  batchDeleteStock: async (ids: string[]) => {
    const response = await apiClient.post<{ deleted: number }>(
      '/crm-integration/stock/batch-delete',
      {
        ids,
      },
    );
    return response.data;
  },

  deleteAllStock: async () => {
    await apiClient.delete('/crm-integration/stock/all');
  },
};

export interface CsvStockRow {
  stock_listing_id?: string;
  registration?: string;
  vin?: string;
  manufacturer?: string;
  model?: string;
  derivative?: string;
  colour?: string;
  year?: string;
  mileage?: string;
  price?: string;
  fuel_type?: string;
  body_type?: string;
  transmission?: string;
  status?: string;
  doors?: string;
  seats?: string;
  condition?: string;
  notes?: string;
  /** Any additional CSV column (e.g. lot) — stored as a custom field and searchable. */
  [key: string]: string | undefined;
}

export interface StockApiToken {
  id: string;
  name: string;
  token_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface StockSearchHit {
  id: string;
  stock_listing_id: string;
  manufacturer: string;
  model: string;
  trim: string | null;
  colour: string | null;
  year: number | null;
  mileage: number | null;
  price: number | null;
  monthly_price: number | null;
  fuel_type: string | null;
  body_type: string | null;
  transmission: string | null;
  registration: string | null;
  doors: number | null;
  seats: number | null;
  owners: number | null;
  status: string | null;
  location: string | null;
  location_phone: string | null;
  images: string[];
  website_url: string | null;
  derivative: string | null;
  engine_size: string | null;
  bhp: number | null;
  condition: string | null;
  drive_train: string | null;
  vat_status: string | null;
  finance_apr: number | null;
  finance_term: number | null;
  finance_deposit: number | null;
  notes: string | null;
  /** Source connector id; null/absent = DealerKit. */
  connector_id?: string | null;
  relevance?: number;
  /** Postcode searches only: straight-line miles from the searched postcode. */
  distance_miles?: number;
  /** Custom CSV fields (e.g. lot_number) are flattened onto the hit at the top level. */
  [key: string]: unknown;
}

export interface StockSearchResult {
  vehicles: StockSearchHit[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  filters_applied: Record<string, unknown>;
  /** Postcode searches: what the postcode resolved to and the radius used (null = nationwide). */
  origin?: { label: string; kind: string; latitude: number; longitude: number };
  radius_miles_used?: number | null;
  /** true when more cars matched than `total` (the count stops at 1,000). */
  total_capped?: boolean;
  error?: string;
}

export interface DealerKitStockListingRow {
  id: string;
  stock_listing_id: string;
  registration: string | null;
  vin: string | null;
  dealer_id: number | null;
  status: string | null;
  /** Set for CRM Integration connector rows; null = DealerKit. */
  connector_id: string | null;
  data: Record<string, unknown> | null;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

export interface StockListingDetail extends DealerKitStockListingRow {
  connector_name: string | null;
  last_seen_at: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface PaginatedDealerKitStockListings {
  data: DealerKitStockListingRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DealerKitStockSyncResult {
  started: boolean;
  message: string;
}

export interface DealerKitStockSyncState {
  id: number | null;
  last_synced_at: string | null;
  last_status: string | null;
  last_count: number | null;
  updated_at: string | null;
  running: boolean;
}
